/**
 * FlippableCardImage — card image with CSS 3D flip animation for DFC cards.
 *
 * For DFCs (transform, modal_dfc, meld, reversible_card, double_faced_token),
 * a hover-revealed flip button triggers a Y-axis rotation to show the back face.
 * Non-DFC cards render as a plain CardImage.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { TbRotate } from 'react-icons/tb';

import type { CardFace, CardLayout, ImageUris } from '@/types/card';
import CardImage, { type CardImageProps } from '../CardImage/CardImage';

/** Layouts where each face has its own image_uris (flippable) */
const FLIPPABLE_LAYOUTS: CardLayout[] = ['transform', 'modal_dfc', 'meld', 'reversible_card', 'double_faced_token', 'art_series'];

export interface FlippableCardImageProps extends Omit<CardImageProps, 'imageUris'> {
  /** Primary (front face) image URIs — used for non-DFC cards too */
  imageUris: Partial<ImageUris> | null;
  /** Card layout string */
  layout?: CardLayout | null;
  /** Card faces — only needed when the card is a DFC */
  cardFaces?: CardFace[] | null;
}

function isFlippable(layout: CardLayout | null | undefined, cardFaces: CardFace[] | null | undefined): boolean {
  if (!layout || !cardFaces || cardFaces.length < 2) return false;
  if (!FLIPPABLE_LAYOUTS.includes(layout)) return false;
  // Both faces must have image URIs
  return cardFaces.every((face) => face.image_uris != null);
}

export default function FlippableCardImage({
  imageUris,
  layout,
  cardFaces,
  name,
  size,
  className,
  ...rest
}: FlippableCardImageProps): ReactElement {
  const [isFlipped, setIsFlipped] = useState(false);
  const flippable = isFlippable(layout, cardFaces);

  const handleFlip = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsFlipped((prev) => !prev);
  }, []);

  // Non-DFC: render plain CardImage
  if (!flippable) {
    return <CardImage imageUris={imageUris} name={name} size={size} className={className} {...rest} />;
  }

  const frontUris = cardFaces![0].image_uris;
  const backUris = cardFaces![1].image_uris;
  const displayName = isFlipped ? cardFaces![1].name : cardFaces![0].name;

  return (
    <div className={clsx('group/flip relative', className)}>
      {/* Rotating container — perspective() in transform avoids creating a stacking context on the parent */}
      <div
        className="relative h-full w-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'perspective(800px) rotateY(180deg)' : 'perspective(800px) rotateY(0deg)',
        }}
      >
        {/* Front face — stays in normal flow so the wrapper gets intrinsic height even when consumers size it with h-auto */}
        <div style={{ backfaceVisibility: 'hidden' }}>
          <CardImage imageUris={frontUris} name={cardFaces![0].name} size={size} className="h-auto! w-full!" {...rest} />
        </div>

        {/* Back face (pre-rotated so it shows correctly when container is flipped) */}
        <div className="absolute inset-0" style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
          <CardImage imageUris={backUris} name={cardFaces![1].name} size={size} className="h-full! w-full!" {...rest} />
        </div>
      </div>

      {/* Flip button — revealed on hover */}
      <button
        type="button"
        onClick={handleFlip}
        className={clsx(
          'absolute top-1 left-1 z-10 flex h-7 w-7 items-center justify-center',
          'bg-void/80 hover:bg-void rounded-full shadow-md',
          'text-ash hover:text-pearl transition-all',
          'opacity-0 group-hover/flip:opacity-100',
          'focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white',
        )}
        aria-label={`Flip card to ${isFlipped ? 'front' : 'back'} face of ${displayName}`}
      >
        <TbRotate className="h-4 w-4" />
      </button>
    </div>
  );
}
